/**
 * Ürün Yönetimi — Liste + Cost/Margin sütun + Maliyet detay drawer + create/edit modal.
 *
 * Her ürün için BOM birim maliyeti (kuruş) gösterilir; düşük margin
 * (< %30) kırmızı uyarı badge. "Maliyet" sütununa tıklayınca drawer açılır,
 * detaylı kırılım (her hammadde × birim cost × line cost) görünür.
 */
import {
  Badge, Group, Image, Loader, Stack, Table, TextInput, Button, ActionIcon,
  Drawer, Modal, Card, Title, Text, Tooltip, Alert, Divider, Select, UnstyledButton,
} from "@mantine/core";
import {
  IconSearch, IconPlus, IconEdit, IconTrash, IconCalculator, IconAlertTriangle,
  IconTrendingUp, IconTrendingDown, IconPhoto, IconChefHat, IconInfoCircle,
  IconChevronUp, IconChevronDown, IconSelector, IconLanguage,
} from "@tabler/icons-react";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCategories, useKdsStations, apiClient, useInfiniteResource } from "../../../shared/api";
import { useRemove } from "../../../shared/api/resources";
import { MoneyDisplay, PageHeader, ResourceForm, confirmDelete, InfiniteTable, EmptyState, Lightbox, ColumnCustomizer, useColumnPrefs, TranslationDrawer, type FormField, type InfiniteTableColumn } from "../../../shared/ui";
import { ProductImageManager } from "../components/ProductImageManager";
import { ProductRecipeDrawer } from "../components/ProductRecipeDrawer";

function buildFields(
  categories: Array<{ id: string; name: string }>,
  kdsStations: Array<{ id: string; code: string; name: string }>,
): FormField[] {
  return [
    { name: "name", label: "Ürün Adı", required: true },
    { name: "sku", label: "SKU", required: true },
    {
      name: "category",
      label: "Kategori",
      type: "select",
      required: true,
      placeholder: "Kategori seç",
      options: categories.map((c) => ({ value: c.id, label: c.name })),
    },
    {
      name: "kds_station",
      label: "Mutfak İstasyonu (KDS)",
      type: "select",
      // Opsiyonel: boş bırakılırsa backend (kds.services.route_order) ürünü
      // VARSAYILAN istasyona yönlendirir — "mutfağa hiç gitmez" DEĞİL.
      clearable: true,
      placeholder: "İstasyon seç (opsiyonel)",
      description: "Boş bırakılırsa ürün varsayılan mutfak istasyonuna düşer",
      // value = istasyon KODU (s.code), id değil — backend kds_station bir
      // CharField kod tutar, route_order bu kodla eşleştirir.
      options: kdsStations.map((s) => ({ value: s.code, label: s.name })),
    },
    { name: "description", label: "Açıklama", type: "textarea" },
    {
      name: "image",
      label: "Kapak Görseli",
      type: "image",
      description: "QR menüde ürün kartında gösterilen ana görsel — ek görseller için 🖼 Galeri",
    },
    { name: "unit_price_minor", label: "Satış Fiyatı", type: "money", required: true, min: 0 },
    {
      name: "is_prepared",
      label: "Hazır Alınan Ürün",
      type: "switch",
      defaultValue: false,
      // Açıksa: kola/ayran/su gibi hazır alınıp satılan ürün — reçetesi (BOM)
      // olmaz, maliyet doğrudan "Alış Maliyeti"nden gelir (mal kabulde son
      // alış fiyatına çekilir). Kapalıysa: reçeteli ürün (burger), maliyet
      // hammaddelerden hesaplanır.
      description: "Kola/ayran/su gibi hazır alınıp satılan ürünler için açın — reçete yerine alış maliyeti kullanılır",
    },
    {
      name: "cost_minor",
      label: "Alış Maliyeti",
      type: "money",
      min: 0,
      defaultValue: 0,
      // Hazır alınıp satılan ürünler (kola, ayran, su...) için doğrudan
      // maliyet. Reçetesi (BOM) olan ürünlerde girilse de yok sayılır —
      // maliyet reçeteden hesaplanır.
      description: "Hazır alınan ürünler için alış fiyatı — reçetesi olan ürünlerde yok sayılır (maliyet reçeteden gelir)",
    },
    {
      name: "track_stock",
      label: "Stok Takibi",
      type: "switch",
      defaultValue: false,
      // Hazır ürünlerde (kola, su...) açılır → mal kabulde stok artar,
      // kritik stok uyarısı çalışır. Reçeteli ürünlerde gereksiz.
      description: "Hazır alınan ürünler için açın — mal kabulde stok adedi takip edilir",
    },
    { name: "vat_rate_percent", label: "KDV %", type: "number", defaultValue: 10, min: 0 },
    { name: "avg_prep_seconds", label: "Ortalama Hazırlık (sn)", type: "number", defaultValue: 300, min: 0 },
    {
      name: "ingredients_text",
      label: "İçindekiler",
      type: "textarea",
      placeholder: "Dana köfte, cheddar peyniri, marul, domates...",
      description: "QR menü ürün detayında gösterilir",
    },
    {
      name: "calories",
      label: "Kalori (kcal)",
      type: "number",
      defaultValue: 0,
      min: 0,
      description: "0 = belirtilmemiş (QR menüde gösterilmez)",
    },
    {
      name: "allergens",
      label: "Alerjenler",
      placeholder: "gluten, süt, yumurta, fındık",
      description: "Virgülle ayırın — QR menüde rozet olarak gösterilir",
    },
    { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
    { name: "is_qr_visible", label: "QR Menüde Görünür", type: "switch", defaultValue: true },
  ];
}

interface CostBreakdown {
  product_id: string;
  product_name: string;
  product_sku: string;
  has_bom: boolean;
  /** Reçetesiz ama Product.cost_minor'dan maliyetli (hazır alınan ürün). */
  is_direct_cost?: boolean;
  bom_id?: string;
  items: Array<{
    ingredient_id: string;
    ingredient_name: string;
    ingredient_sku: string;
    unit: string;
    quantity: string;
    unit_cost_minor: number;
    line_cost_minor: number;
    supplier_name: string;
    has_supplier: boolean;
  }>;
  total_cost_minor: number;
  missing_costs: string[];
  unit_price_minor: number;
  // Maliyet bilinmiyorsa (total_cost_minor=0) marj alanları null gelir —
  // sahte %100 yerine "—" gösterilir.
  margin_minor: number | null;
  margin_percent: number | null;
  is_profitable: boolean | null;
  is_low_margin: boolean | null;
}

// Sıralanabilir sütun anahtarları — "cost" liste verisinde değil, satır
// bazlı cost-breakdown çağrısından gelir (bkz. costMap lifting).
type SortKey = "name" | "sku" | "category" | "price" | "cost";

// Kolon özelleştirme — kullanıcı görünürlük + sırayı seçer (localStorage).
// "actions" kolonu listede DEĞİL: hep görünür ve sonda (kilitli).
const PRODUCT_COLUMN_IDS = [
  "image", "name", "sku", "category", "kds_station",
  "price", "cost", "margin", "status",
];
const PRODUCT_COLUMN_LABELS: Record<string, string> = {
  image: "Görsel",
  name: "Ad",
  sku: "SKU",
  category: "Kategori",
  kds_station: "KDS İstasyonu",
  price: "Satış Fiyatı",
  cost: "Birim Maliyet",
  margin: "Marj",
  status: "Durum",
};

export function ProductsPage() {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [debouncedSearch] = useDebouncedValue(search.trim(), 300);
  const productFilters: Record<string, any> = {};
  if (categoryFilter) productFilters.category = categoryFilter;
  const {
    items: productItems,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("products", { search: debouncedSearch, filters: productFilters });
  const { data: categories } = useCategories();
  const { data: kdsStations } = useKdsStations();
  const remove = useRemove("products");

  const categoryList = (categories?.results || categories || []) as Array<{
    id: string; name: string; parent: string | null;
  }>;
  const stationList = (kdsStations?.results || kdsStations || []) as Array<{ id: string; code: string; name: string }>;
  const FIELDS = buildFields(categoryList, stationList);
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [costProductId, setCostProductId] = useState<string | null>(null);
  const [galleryProduct, setGalleryProduct] = useState<any | null>(null);
  const [recipeProduct, setRecipeProduct] = useState<any | null>(null);
  // EN çeviri düzenleme drawer'ı için seçili ürün.
  const [translateProduct, setTranslateProduct] = useState<any | null>(null);
  // Listede görsele tıklayınca açılan lightbox — o ürünün kapak + galeri
  // görsellerinin tam-boy URL'leri.
  const [lightboxImages, setLightboxImages] = useState<string[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const catMap = new Map<string, string>();
  categoryList.forEach((c) => catMap.set(c.id, c.name));

  // Ürünün kds_station alanı istasyon KODUNU tutar — listede okunaklı isim
  // göstermek için kod → isim haritası.
  const stationMap = new Map<string, string>();
  stationList.forEach((s) => stationMap.set(s.code, s.name));

  // Birim maliyet her satırda ayrı cost-breakdown çağrısıyla gelir; "Birim
  // Maliyet" sütununa göre sıralayabilmek için satırlar değeri yüklendikçe
  // buraya bildirir (lift state). Henüz yüklenmemiş ürünler sıralamada sona.
  const [costMap, setCostMap] = useState<Record<string, number | null>>({});
  const reportCost = useCallback((id: string, cost: number | null) => {
    setCostMap((prev) => (prev[id] === cost ? prev : { ...prev, [id]: cost }));
  }, []);

  // Kolon görünürlük + sıra tercihi (kalıcı).
  const columnPrefs = useColumnPrefs("products.columns", PRODUCT_COLUMN_IDS);

  // Üst kategoriler (parent === null) Mantine Select'te <optgroup> ile
  // gruplanır → kullanıcı hangi grubun altında olduğunu görür ve üst
  // kategori başlığı belirgin görünür.
  const categoryOptions = useMemo(() => {
    const roots = categoryList.filter((c) => c.parent === null);
    const childrenOf = (pid: string) => categoryList.filter((c) => c.parent === pid);
    const groups = roots.map((root) => {
      const kids = childrenOf(root.id);
      return {
        group: root.name,
        // Üst kategorinin kendisi de seçilebilir (alt ağacı kapsar) —
        // başına ▸ koyarak "tüm <isim>" anlamını belirginleştiriyoruz.
        items: [
          { value: root.id, label: `▸ Tüm ${root.name}` },
          ...kids.map((k) => ({ value: k.id, label: k.name })),
        ],
      };
    });
    // Hiç parent'ı olmayan ama child'ı da olmayan kök kategoriler zaten
    // yukarıda kendi grubunu oluşturur; ekstra "diğer" gruba gerek yok.
    return groups;
  }, [categoryList]);

  const rows = productItems;
  const sortedRows = useMemo(() => {
    const dir = sortAsc ? 1 : -1;
    const value = (p: any): string | number => {
      switch (sortKey) {
        case "sku": return (p.sku || "").toLowerCase();
        case "category": return (catMap.get(p.category) || p.category_name || "").toLowerCase();
        case "price": return p.unit_price_minor ?? 0;
        case "cost": {
          const c = costMap[p.id];
          // Maliyeti olmayan/henüz yüklenmemiş ürünler her zaman sona.
          return c == null ? (sortAsc ? Infinity : -Infinity) : c;
        }
        default: return (p.name || "").toLowerCase();
      }
    };
    return [...rows].sort((a, b) => {
      const va = value(a), vb = value(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    // catMap her render'da yeniden kurulduğundan bağımlılığa koymuyoruz;
    // categories değişince products zaten yeniden çekilir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortAsc, costMap]);

  const toggleSort = useCallback((key: SortKey) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortAsc((a) => !a);
        return prevKey;
      }
      setSortAsc(true);
      return key;
    });
  }, []);

  async function handleDelete(id: string) {
    if (!(await confirmDelete("Ürün silinsin mi?"))) return;
    try {
      await remove.mutateAsync(id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  /** Ürünün tüm görsellerini (kapak + galeri, tam-boy) lightbox'ta açar. */
  function openLightbox(product: any) {
    const urls: string[] = [
      ...(product.image ? [product.image] : []),
      ...((product.gallery_images || []).map((g: any) => g.image)),
    ];
    if (urls.length === 0) return;
    setLightboxImages(urls);
    setLightboxIndex(0);
  }

  // Kolonlar id→tanım olarak; kullanıcı görünürlük + sırayı seçer (kalıcı,
  // localStorage). "actions" kolonu kilitli: gizlenemez, hep sonda.
  const allColumns: Record<string, InfiniteTableColumn<any>> = {
    image: {
      header: "Görsel",
      width: 56,
      render: (p) =>
        (p.image_thumbnail || p.image) ? (
          <Tooltip label="Büyüt" withinPortal>
            <Image
              src={p.image_thumbnail || p.image}
              h={40} w={40} radius="sm" fit="cover" alt={p.name}
              style={{ cursor: "zoom-in" }}
              onClick={() => openLightbox(p)}
            />
          </Tooltip>
        ) : (
          <Group justify="center" w={40} h={40} bg="gray.1" style={{ borderRadius: 4 }}>
            <IconPhoto size={16} color="var(--mantine-color-gray-5)" />
          </Group>
        ),
    },
    name: {
      header: <SortableHeader label="Ad" sortKey="name" active={sortKey} asc={sortAsc} onSort={toggleSort} />,
      render: (p) => <Text fw={500} span>{p.name}</Text>,
    },
    sku: {
      header: <SortableHeader label="SKU" sortKey="sku" active={sortKey} asc={sortAsc} onSort={toggleSort} />,
      render: (p) => (
        <Badge variant="light" styles={{ root: { maxWidth: "unset" }, label: { overflow: "visible" } }}>
          {p.sku}
        </Badge>
      ),
    },
    category: {
      header: <SortableHeader label="Kategori" sortKey="category" active={sortKey} asc={sortAsc} onSort={toggleSort} />,
      render: (p) => catMap.get(p.category) || p.category_name || "-",
    },
    kds_station: {
      header: "KDS İstasyonu",
      render: (p) =>
        p.kds_station ? (
          <Badge variant="light" color="cyan" styles={{ root: { maxWidth: "unset" }, label: { overflow: "visible" } }}>
            {stationMap.get(p.kds_station) || p.kds_station}
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">—</Text>
        ),
    },
    price: {
      header: <SortableHeader label="Satış Fiyatı" sortKey="price" active={sortKey} asc={sortAsc} onSort={toggleSort} />,
      align: "right",
      render: (p) => <Text fw={700} span><MoneyDisplay minor={p.unit_price_minor} /></Text>,
    },
    cost: {
      header: <SortableHeader label="Birim Maliyet" sortKey="cost" active={sortKey} asc={sortAsc} onSort={toggleSort} />,
      align: "right",
      render: (p) => (
        <ProductCostCell product={p} onCostLoaded={reportCost} onCost={() => setCostProductId(p.id)} />
      ),
    },
    margin: {
      header: "Marj",
      align: "right",
      render: (p) => <ProductMarginCell product={p} />,
    },
    status: {
      header: "Durum",
      render: (p) => (
        <Group gap="xs" wrap="nowrap">
          <Badge color={p.is_active ? "green" : "gray"} styles={{ root: { maxWidth: "unset" }, label: { overflow: "visible" } }}>
            {p.is_active ? "Aktif" : "Pasif"}
          </Badge>
          {p.is_qr_visible && (
            <Badge color="violet" variant="light" styles={{ root: { maxWidth: "unset" }, label: { overflow: "visible" } }}>
              QR
            </Badge>
          )}
          <StockTrackingBadge product={p} />
        </Group>
      ),
    },
    actions: {
      header: "",
      render: (p) => {
        const galleryCount = (p.image ? 1 : 0) + (p.gallery_images?.length || 0);
        return (
          <Group gap={4} justify="flex-end" wrap="nowrap">
            {!p.is_prepared ? (
              <Tooltip label="Reçete (BOM)">
                <ActionIcon variant="subtle" color="teal" onClick={() => setRecipeProduct(p)}>
                  <IconChefHat size={16} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <ActionIcon variant="transparent" aria-hidden style={{ visibility: "hidden", pointerEvents: "none" }}>
                <IconChefHat size={16} />
              </ActionIcon>
            )}
            <Tooltip label="Maliyet Detayı">
              <ActionIcon variant="subtle" color="blue" onClick={() => setCostProductId(p.id)}>
                <IconCalculator size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={`Görseller (${galleryCount}/4)`}>
              <ActionIcon variant="subtle" color="violet" onClick={() => setGalleryProduct(p)}>
                <IconPhoto size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Çeviriler (EN)">
              <ActionIcon variant="subtle" color="grape" onClick={() => setTranslateProduct(p)}>
                <IconLanguage size={16} />
              </ActionIcon>
            </Tooltip>
            <ActionIcon variant="subtle" onClick={() => { setEditing(p); open(); }}>
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(p.id)}>
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        );
      },
    },
  };
  // actions kolonu özelleştirmeye dahil değil: hep görünür ve en sonda.
  const tableColumns = [
    ...columnPrefs.orderedVisibleIds.map((id) => allColumns[id]).filter(Boolean),
    allColumns.actions,
  ];

  return (
    // Sayfa, Main'in kalan yüksekliğini doldurur → tablo "fill" modunda kendi
    // içinde kayar, sayfa ayrıca kaymaz (çift scroll yok). minHeight:0 flex
    // çocuğun taşmasına izin verir.
    <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
      <PageHeader
        title="Ürünler"
        subtitle="Menü ürünlerinizi ve birim maliyetlerinizi yönetin"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>
            Yeni Ürün
          </Button>
        }
      />
      <Group gap="sm" align="flex-end" wrap="nowrap">
        <TextInput
          style={{ flex: 1 }}
          placeholder="Ürün ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <Select
          style={{ flex: 1 }}
          placeholder="Tüm kategoriler"
          data={categoryOptions}
          value={categoryFilter}
          onChange={setCategoryFilter}
          clearable
          searchable
        />
        <ColumnCustomizer labels={PRODUCT_COLUMN_LABELS} prefs={columnPrefs} />
      </Group>
      <Card
        padding="sm" radius="md" withBorder
        style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <InfiniteTable
          rows={sortedRows}
          rowKey={(p) => p.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          striped
          maxBodyHeight="fill"
          emptyContent={
            <EmptyState title={debouncedSearch ? "Eşleşen ürün yok" : "Ürün yok"} />
          }
          columns={tableColumns}
        />
      </Card>

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="products"
        title={editing ? "Ürün Düzenle" : "Yeni Ürün"}
        fields={FIELDS}
        editId={editing?.id || null}
        initialValues={editing || undefined}
      />

      <TranslationDrawer
        opened={!!translateProduct}
        onClose={() => setTranslateProduct(null)}
        resource="products"
        id={translateProduct?.id}
        title={translateProduct ? `${translateProduct.name} — Çeviriler (EN)` : "Çeviriler (EN)"}
        baseValues={{
          name: translateProduct?.name,
          description: translateProduct?.description,
          ingredients_text: translateProduct?.ingredients_text,
          allergens: translateProduct?.allergens,
        }}
        translations={translateProduct?.translations}
        invalidateKeys={[["products"]]}
        fields={[
          { name: "name", label: "Ad (EN)" },
          { name: "description", label: "Açıklama (EN)", textarea: true },
          { name: "ingredients_text", label: "İçindekiler (EN)", textarea: true },
          { name: "allergens", label: "Alerjenler (EN) — virgülle ayır" },
        ]}
      />

      <CostBreakdownDrawer
        productId={costProductId}
        onClose={() => setCostProductId(null)}
      />

      <ProductRecipeDrawer
        product={recipeProduct}
        onClose={() => setRecipeProduct(null)}
      />

      <Modal
        opened={!!galleryProduct}
        onClose={() => setGalleryProduct(null)}
        title={galleryProduct ? `🖼 ${galleryProduct.name} — Görseller` : ""}
        size="xl"
      >
        <ProductImageManager
          productId={galleryProduct?.id || null}
          coverImage={galleryProduct?.image || null}
        />
      </Modal>

      {lightboxImages && (
        <Lightbox
          images={lightboxImages}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxImages(null)}
        />
      )}
    </Stack>
  );
}

/**
 * Sıralanabilir başlık içeriği — tıklayınca yön değişir, aktif sütun ok ile.
 * InfiniteTable kendi <Table.Th> sarmalayıcısını render ettiğinden burada
 * yalnızca tıklanabilir iç içerik döndürülür (iç içe <th> olmaması için).
 */
function SortableHeader({
  label, sortKey, active, asc, onSort,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  asc: boolean;
  onSort: (k: SortKey) => void;
}) {
  const isActive = active === sortKey;
  const Icon = !isActive ? IconSelector : asc ? IconChevronUp : IconChevronDown;
  return (
    <UnstyledButton
      onClick={() => onSort(sortKey)}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: "inherit" }}
    >
      <Text size="sm" fw={isActive ? 700 : 500} span>{label}</Text>
      <Icon size={14} color={isActive ? "var(--mantine-color-blue-6)" : "var(--mantine-color-gray-5)"} />
    </UnstyledButton>
  );
}

/**
 * Ürün başına BOM cost-breakdown query'si. Aynı queryKey kullanan tüm
 * hücreler react-query tarafından dedupe edilir → ürün başına tek istek.
 */
function useProductBreakdown(productId: string) {
  return useQuery<CostBreakdown>({
    queryKey: ["product-cost", productId],
    queryFn: async () => (await apiClient.get(`/api/v1/products/${productId}/cost-breakdown/`)).data,
    staleTime: 60_000,
  });
}

/** "Birim Maliyet" hücresi — değeri yüklenince parent'a raporlar (sıralama için). */
function ProductCostCell({
  product, onCostLoaded, onCost,
}: {
  product: any;
  onCostLoaded: (id: string, cost: number | null) => void;
  onCost: () => void;
}) {
  const { data: breakdown } = useProductBreakdown(product.id);

  // Maliyet "biliniyor mu" = gerçek tutar > 0. Boş reçete (has_bom ama
  // malzemesiz) veya maliyetsiz hazır ürün → 0 → bilinmiyor.
  const hasCost = !!breakdown && breakdown.total_cost_minor > 0;

  // Maliyet yüklenince parent'a bildir — "Birim Maliyet" sütununa göre
  // sıralama bu değerleri kullanır. Maliyetsiz ürün = null (sona gider).
  useEffect(() => {
    if (breakdown) {
      onCostLoaded(product.id, breakdown.total_cost_minor > 0 ? breakdown.total_cost_minor : null);
    }
  }, [breakdown, product.id, onCostLoaded]);

  if (!breakdown) return <Loader size="xs" />;
  if (!hasCost) {
    return (
      <Tooltip label="Maliyet tanımlanmamış — reçete ekleyin veya alış maliyeti girin">
        <Text size="xs" c="dimmed">—</Text>
      </Tooltip>
    );
  }
  return (
    <Tooltip label={breakdown.is_direct_cost ? "Doğrudan alış maliyeti" : "Maliyet detayını gör"}>
      <Group gap={4} justify="flex-end" style={{ cursor: "pointer" }} onClick={onCost}>
        <MoneyDisplay minor={breakdown.total_cost_minor} fw={600} />
        {breakdown.missing_costs.length > 0 && (
          <Tooltip label={`Eksik: ${breakdown.missing_costs.join(", ")}`}>
            <IconAlertTriangle size={14} color="orange" />
          </Tooltip>
        )}
      </Group>
    </Tooltip>
  );
}

/** "Marj" hücresi — aynı cost-breakdown query'sinden margin badge çizer. */
// Stok takibi rozeti — sipariş onayında stok düşüp düşmeyeceğini gösterir.
// Kural pos.deduct_order_stock ile birebir: reçeteli ürün (BOM) hammaddesini
// düşer, track_stock ürün kendi stoğunu düşer; ikisi de yoksa stok DÜŞMEZ.
// Bu yüzden "takipli" = has_bom || track_stock. Tek bakışta "stok takibi var
// mı yok mu" — "domates neden düşmüyor?" sorusunu listede yanıtlar.
function StockTrackingBadge({ product }: { product: any }) {
  const tracked = product.has_bom || product.track_stock;
  if (tracked) {
    return (
      <Tooltip
        label={
          product.has_bom
            ? "Stok takipli (reçeteli) — sipariş onayında hammaddeler stoktan düşer"
            : "Stok takipli — sipariş onayında ürün stoğu düşer"
        }
        withinPortal
      >
        <Badge color="teal" variant="light"
          styles={{ root: { maxWidth: "unset" }, label: { overflow: "visible" } }}>
          Stok takipli
        </Badge>
      </Tooltip>
    );
  }
  return (
    <Tooltip label="Stok takibi yok — sipariş verilince stok düşmez. Reçete ekleyin veya 'Stok Takibi'ni açın." withinPortal>
      <Badge color="orange" variant="light" leftSection={<IconAlertTriangle size={11} />}
        styles={{ root: { maxWidth: "unset" }, label: { overflow: "visible" } }}>
        Stok takipsiz
      </Badge>
    </Tooltip>
  );
}

function ProductMarginCell({ product }: { product: any }) {
  const { data: breakdown } = useProductBreakdown(product.id);
  // Maliyet bilinmiyorsa marj alanları null gelir — badge gösterme.
  if (breakdown && breakdown.margin_minor != null && breakdown.margin_percent != null) {
    return <MarginBadge minor={breakdown.margin_minor} percent={breakdown.margin_percent} />;
  }
  return <Text size="xs" c="dimmed">—</Text>;
}

function MarginBadge({ minor, percent }: { minor: number; percent: number }) {
  if (minor < 0) {
    return (
      <Badge color="red" leftSection={<IconTrendingDown size={12} />}>
        ZARAR %{Math.abs(percent).toFixed(0)}
      </Badge>
    );
  }
  if (percent < 20) {
    return (
      <Badge color="orange" leftSection={<IconAlertTriangle size={12} />}>
        Düşük %{percent.toFixed(0)}
      </Badge>
    );
  }
  if (percent < 40) {
    return (
      <Badge color="yellow">%{percent.toFixed(0)}</Badge>
    );
  }
  return (
    <Badge color="green" leftSection={<IconTrendingUp size={12} />}>
      %{percent.toFixed(0)}
    </Badge>
  );
}

function CostBreakdownDrawer({
  productId, onClose,
}: { productId: string | null; onClose: () => void }) {
  const { data: breakdown, isLoading } = useQuery<CostBreakdown>({
    queryKey: ["product-cost-detail", productId],
    queryFn: async () => (await apiClient.get(`/api/v1/products/${productId}/cost-breakdown/`)).data,
    enabled: !!productId,
  });

  return (
    <Drawer
      opened={!!productId}
      onClose={onClose}
      position="right"
      size="lg"
      title={breakdown ? `📊 ${breakdown.product_name} — Maliyet Detayı` : "Yükleniyor..."}
    >
      {isLoading || !breakdown ? <Loader /> : (!breakdown.has_bom && !breakdown.is_direct_cost) ? (
        <Alert color="orange" icon={<IconAlertTriangle size={18} />}>
          Bu ürün için maliyet tanımlanmamış. İki yol var:
          <br />
          • <strong>Üretilen ürün</strong> (burger, pizza...) → <strong>Reçete (BOM)</strong> sayfasından reçete ekleyin.
          <br />
          • <strong>Hazır alınan ürün</strong> (kola, ayran...) → ürünü düzenleyip <strong>Alış Maliyeti</strong> girin.
        </Alert>
      ) : (
        <Stack gap="md">
          {breakdown.is_direct_cost && (
            <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />}>
              Bu ürün <strong>hazır alınıp satılan</strong> bir ürün — maliyeti
              doğrudan alış fiyatından gelir, reçetesi yoktur.
            </Alert>
          )}
          {breakdown.missing_costs.length > 0 && (
            <Alert color="orange" icon={<IconAlertTriangle size={18} />}>
              <strong>Eksik maliyet bilgisi:</strong> {breakdown.missing_costs.join(", ")}
              <br />
              Bu hammaddeler için tedarikçi fiyatı veya manuel <Code>last_cost_minor</Code>
              tanımlayın, aksi halde maliyet eksik hesaplanır.
            </Alert>
          )}

          {/* Boş reçete uyarısı — reçete var ama hiç malzeme yok. */}
          {breakdown.has_bom && breakdown.items.length === 0 && (
            <Alert color="orange" icon={<IconAlertTriangle size={18} />}>
              Bu ürünün reçetesi var ama içine malzeme eklenmemiş — maliyet
              hesaplanamıyor. <strong>Reçete (BOM)</strong> sayfasından malzeme ekleyin.
            </Alert>
          )}

          {/* Hammadde tablosu — yalnız reçeteli ürünlerde. Hazır alınan
              ürünlerde (is_direct_cost) hammadde kırılımı yoktur. */}
          {breakdown.has_bom && breakdown.items.length > 0 && (
            <Card padding="md" radius="md" withBorder>
              <Title order={5} mb="sm">Hammadde Kırılımı</Title>
              <Table.ScrollContainer minWidth={480}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Malzeme</Table.Th>
                    <Table.Th>Tedarikçi</Table.Th>
                    <Table.Th ta="right">Miktar</Table.Th>
                    <Table.Th ta="right">Birim Maliyet</Table.Th>
                    <Table.Th ta="right">Satır</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {breakdown.items.map((item) => (
                    <Table.Tr key={item.ingredient_id}>
                      <Table.Td>
                        <Stack gap={0}>
                          <Text fw={500} size="sm">{item.ingredient_name}</Text>
                          <Text size="xs" c="dimmed">{item.ingredient_sku}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        {item.supplier_name ? (
                          <Badge variant="light" size="sm">{item.supplier_name}</Badge>
                        ) : (
                          <Badge color="orange" variant="light" size="sm">Tedarikçi yok</Badge>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">{formatQty(item.quantity)} {item.unit}</Table.Td>
                      <Table.Td ta="right" c="dimmed">
                        <MoneyDisplay minor={item.unit_cost_minor} />
                        <Text size="xs" c="dimmed">/ {item.unit}</Text>
                      </Table.Td>
                      <Table.Td ta="right" fw={600}>
                        <MoneyDisplay minor={item.line_cost_minor} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </Table.ScrollContainer>
            </Card>
          )}

          {/* Toplam + Margin */}
          <Card padding="lg" radius="md" withBorder bg="gray.0">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text fw={600}>Birim Maliyet (toplam)</Text>
                <MoneyDisplay minor={breakdown.total_cost_minor} fw={700} size="lg" />
              </Group>
              <Group justify="space-between">
                <Text fw={600}>Satış Fiyatı</Text>
                <MoneyDisplay minor={breakdown.unit_price_minor} fw={700} size="lg" c="blue" />
              </Group>
              <Divider my="sm" />
              <Group justify="space-between">
                <Text fw={700} size="lg">Net Marj</Text>
                {/* Maliyet bilinmiyorsa marj hesaplanamaz — sahte değer yerine "—" */}
                {breakdown.margin_minor != null && breakdown.margin_percent != null ? (
                  <Group gap="sm">
                    <MoneyDisplay
                      minor={breakdown.margin_minor}
                      fw={800}
                      size="xl"
                      c={breakdown.is_profitable ? "green" : "red"}
                    />
                    <MarginBadge minor={breakdown.margin_minor} percent={breakdown.margin_percent} />
                  </Group>
                ) : (
                  <Text c="dimmed">— (maliyet girilmemiş)</Text>
                )}
              </Group>
            </Stack>
          </Card>

          {/* Öneri */}
          {breakdown.is_low_margin && breakdown.margin_percent != null && (
            <Alert color="orange" icon={<IconAlertTriangle size={18} />}>
              <Text fw={600}>⚠️ Düşük margin uyarısı</Text>
              <Text size="sm" mt="xs">
                Bu ürünün margin'i %{breakdown.margin_percent.toFixed(1)}. Hammadde
                maliyetleriniz artmış olabilir. <strong>Satış fiyatını manuel
                güncelleyerek</strong> kârlılığınızı koruyabilirsiniz.
              </Text>
              <Text size="sm" mt="xs">
                Önerilen yeni fiyat (%30 margin için):{" "}
                <strong>
                  <MoneyDisplay minor={Math.ceil(breakdown.total_cost_minor / 0.7)} />
                </strong>
              </Text>
            </Alert>
          )}

          {breakdown.is_profitable === false && breakdown.margin_minor != null && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              <Text fw={600}>🚨 BU ÜRÜN ZARARLI SATIYORSUNUZ</Text>
              <Text size="sm" mt="xs">
                Birim maliyetiniz satış fiyatından yüksek. Her satışta{" "}
                <MoneyDisplay minor={Math.abs(breakdown.margin_minor)} fw={700} />{" "}
                zarar ediyorsunuz. Acil fiyat güncellemesi gerekli.
              </Text>
            </Alert>
          )}
        </Stack>
      )}
    </Drawer>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return <code style={{ background: "rgba(0,0,0,0.05)", padding: "2px 6px", borderRadius: 4 }}>{children}</code>;
}

/** "30.000" → "30", "1.500" → "1.5" — DecimalField'in fazla sıfırlarını at. */
function formatQty(q: string | number): string {
  const n = Number(q);
  return Number.isFinite(n) ? String(n) : String(q);
}
